import { cn } from '@trakd/ui';
import type { ReactElement } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Linkify } from '@/lib/text/linkify';

export interface TrakdChatMessageRowProps {
  role: string;
  content: string;
  /** Quando presente, exibe um label acima da bolha do assistente (ex.: nome do atendente). */
  authorLabel?: string;
}

/**
 * Custom renderers para react-markdown, alinhados com o design system Trakd
 * (violet brand, surface tokens, glass). User não escreve markdown -> só usado
 * em mensagens do assistant.
 */
const markdownComponents: Components = {
  a: ({ children, href, ...props }) => (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-trakd-brand decoration-trakd-brand/40 hover:decoration-trakd-brand underline underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children, className, ...props }) => {
    // Inline code (sem language-* class) vs bloco (dentro de <pre>, com language-*)
    const isInline = !className?.startsWith('language-');
    if (isInline) {
      return (
        <code {...props} className="bg-surface-2 rounded px-1.5 py-0.5 font-mono text-xs">
          {children}
        </code>
      );
    }
    return (
      <code {...props} className={cn('font-mono text-xs', className)}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre {...props} className="bg-surface-2 my-2 overflow-x-auto rounded-lg p-3 text-xs">
      {children}
    </pre>
  ),
  table: ({ children, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table {...props} className="w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th {...props} className="border-border bg-surface-2 border px-2 py-1 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td {...props} className="border-border border px-2 py-1">
      {children}
    </td>
  ),
  ul: ({ children, ...props }) => (
    <ul {...props} className="my-2 list-disc space-y-1 pl-5">
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol {...props} className="my-2 list-decimal space-y-1 pl-5">
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li {...props} className="leading-relaxed">
      {children}
    </li>
  ),
  h1: ({ children, ...props }) => (
    <h1 {...props} className="mt-3 mb-2 text-base font-semibold">
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 {...props} className="mt-3 mb-1.5 text-sm font-semibold">
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 {...props} className="mt-2 mb-1 text-sm font-semibold">
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p {...props} className="my-1.5 leading-relaxed first:mt-0 last:mb-0">
      {children}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong {...props} className="font-semibold">
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em {...props} className="italic">
      {children}
    </em>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      {...props}
      className="border-trakd-brand/40 text-ink-muted my-2 border-l-2 pl-3 italic"
    >
      {children}
    </blockquote>
  ),
  hr: (props) => <hr {...props} className="border-border-subtle my-3" />,
};

/**
 * Bubble de chat · estilo Linear/Raycast:
 * - Assistant: bubble glass com avatar pequeno violet à esquerda, conteúdo em markdown
 * - User: bubble com tint violet sutil (não cor sólida chapada), alinhado à direita, texto puro
 */
export function TrakdChatMessageRow({
  role,
  content,
  authorLabel,
}: TrakdChatMessageRowProps): ReactElement {
  const isUser = role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-violet/15 ring-violet/25 text-ink max-w-[80%] rounded-2xl rounded-tr-md px-4 py-2.5 text-sm leading-relaxed ring-1">
          <p className="whitespace-pre-wrap">
            <Linkify
              text={content}
              linkClassName="decoration-ink/40 hover:decoration-ink underline underline-offset-2"
            />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <span
        aria-hidden
        className="ring-violet/40 mt-0.5 h-6 w-6 shrink-0 overflow-hidden rounded-full bg-contain bg-center bg-no-repeat ring-2"
        style={{ backgroundImage: 'url(/icons/icon-192.png)' }}
      />
      <div className="max-w-[85%] min-w-0">
        {authorLabel && (
          <p className="text-ink-faint mb-1 text-[10px] font-medium">{authorLabel}</p>
        )}
        <div
          className={cn(
            'text-ink rounded-2xl rounded-tl-md px-4 py-2.5 text-sm leading-relaxed',
            'bg-surface-elevated/60 ring-border-subtle/60 ring-1 backdrop-blur-sm',
          )}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
